
import { useCallback } from 'react'
import { type ReactFlowInstance } from '@xyflow/react'
import { apiCall } from '@/lib/api/api-helpers'
import { useJobStore, type JobRecord } from '@/stores/jobStore'
import type { JobType } from '@genieartinc/schemas'
import { usePostHog } from 'posthog-js/react'

interface CreateJobParams {
  type: JobType
  canvasId: string
  nodeId: string
  input: {
    prompt: string
    modelId: string
    settings?: Record<string, unknown>
    variantCount?: number
  }
}

interface UseJobActionsParams {
  canvasRef: React.RefObject<ReactFlowInstance | null>
}

/**
 * Hook for job actions (create, cancel, retry, navigate to node).
 *
 * Provides async functions that call the job API endpoints and
 * optimistically update the job store.
 */
export function useJobActions({ canvasRef }: UseJobActionsParams) {
  const addJob = useJobStore((s) => s.addJob)
  const updateJobStatus = useJobStore((s) => s.updateJobStatus)
  const posthog = usePostHog()

  const createJob = useCallback(
    async (params: CreateJobParams): Promise<string | null> => {
      try {
        const result = (await apiCall('/api/jobs/create', true, {
          method: 'POST',
          body: JSON.stringify(params),
        })) as {
          success: boolean
          data: {
            jobId: string
            status: 'PENDING'
            creditsCharged: number
            createdAt: string
          }
        }

        if (result.success && result.data) {
          const job: JobRecord = {
            id: result.data.jobId,
            type: params.type,
            status: 'PENDING',
            nodeId: params.nodeId,
            canvasId: params.canvasId,
            progress: 0,
            createdAt: result.data.createdAt,
          }
          addJob(job)
          posthog.capture('ai_job_created', {
            job_id: result.data.jobId,
            job_type: params.type,
            canvas_id: params.canvasId,
            node_id: params.nodeId,
            model_id: params.input.modelId,
            credits_charged: result.data.creditsCharged,
          })
          return result.data.jobId
        }

        return null
      } catch (err) {
        console.error('Failed to create job:', err)
        return null
      }
    },
    [addJob, posthog]
  )

  const cancelJob = useCallback(
    async (jobId: string): Promise<boolean> => {
      try {
        const result = (await apiCall(`/api/jobs/${jobId}/cancel`, true, {
          method: 'POST',
        })) as {
          success: boolean
          data: { jobId: string; status: 'CANCELED'; creditsRefunded: number }
        }

        if (result.success) {
          updateJobStatus(jobId, 'CANCELED')
          posthog.capture('ai_job_cancelled', {
            job_id: jobId,
            credits_refunded: result.data.creditsRefunded,
          })
          return true
        }

        return false
      } catch (err) {
        console.error('Failed to cancel job:', err)
        return false
      }
    },
    [updateJobStatus, posthog]
  )

  const retryJob = useCallback(
    async (jobId: string): Promise<string | null> => {
      try {
        const result = (await apiCall(`/api/jobs/${jobId}/retry`, true, {
          method: 'POST',
        })) as {
          success: boolean
          data: {
            newJobId: string
            originalJobId: string
            status: 'PENDING'
            creditsCharged: number
          }
        }

        if (result.success && result.data) {
          // Get original job info for the new record
          const originalJob = useJobStore.getState().jobs.get(jobId)
          if (originalJob) {
            const newJob: JobRecord = {
              id: result.data.newJobId,
              type: originalJob.type,
              status: 'PENDING',
              nodeId: originalJob.nodeId,
              canvasId: originalJob.canvasId,
              progress: 0,
              createdAt: new Date().toISOString(),
            }
            addJob(newJob)
          }
          posthog.capture('ai_job_retried', {
            original_job_id: jobId,
            new_job_id: result.data.newJobId,
            credits_charged: result.data.creditsCharged,
          })
          return result.data.newJobId
        }

        return null
      } catch (err) {
        console.error('Failed to retry job:', err)
        return null
      }
    },
    [addJob, posthog]
  )

  const goToNode = useCallback(
    (nodeId: string) => {
      if (!canvasRef.current) return

      const node = canvasRef.current.getNode(nodeId)
      if (!node) return

      canvasRef.current.fitView({
        nodes: [node],
        duration: 300,
        padding: 0.5,
      })
    },
    [canvasRef]
  )

  return { createJob, cancelJob, retryJob, goToNode }
}
